import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import type {
  AuthAccessibleSite,
  AuthState,
  AuthSubscription,
  AuthUser,
  AuthSiteSummary,
} from '~/types/auth'
import type { EffectiveModule, ResolvedSubscription } from '~~/types/tenant'
import { useOwnerModules, useOwnerSiteId } from '~/composables/useSiteModules'

export const AUTH_PHONE_KEY = 'aradcore:phone'

type OwnerSiteBound = {
  modules: Ref<EffectiveModule[]>
  siteId: Ref<string | null>
}

function defaultSubscription(): AuthSubscription {
  return {
    status: 'none',
    plan: null,
    expiresAt: null,
  }
}

function mapSubscription(sub: ResolvedSubscription | null | undefined): AuthSubscription {
  if (!sub) return defaultSubscription()
  const status = sub.status === 'active' ? 'active' : sub.status === 'pending' ? 'none' : sub.status
  return {
    status,
    plan: sub.planKey,
    planTitle: sub.planName?.fa || sub.planName?.en,
    expiresAt: sub.periodEnd,
  }
}

/** Capture `useState` refs before any await — Nuxt context is lost after await. */
function bindOwnerSite(): OwnerSiteBound {
  return {
    modules: useOwnerModules(),
    siteId: useOwnerSiteId(),
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    phone: null,
    isAuthenticated: false,
    user: null,
    subscription: defaultSubscription(),
    site: null,
    sites: [],
    hydrated: false,
  }),

  getters: {
    hasActiveSubscription(state): boolean {
      if (state.subscription.status !== 'active' && state.subscription.status !== 'trial') return false
      if (!state.subscription.expiresAt) return false
      return new Date(state.subscription.expiresAt).getTime() > Date.now()
    },
  },

  actions: {
    setPhone(phone: string) {
      this.phone = phone
      if (import.meta.client) localStorage.setItem(AUTH_PHONE_KEY, phone)
    },

    reset(bound?: OwnerSiteBound) {
      const owner = bound ?? bindOwnerSite()
      this.phone = import.meta.client ? localStorage.getItem(AUTH_PHONE_KEY) : this.phone
      this.isAuthenticated = false
      this.user = null
      this.subscription = defaultSubscription()
      this.site = null
      this.sites = []
      owner.modules.value = []
      owner.siteId.value = null
    },

    applyMe(
      payload: {
        user: AuthUser
        subscription?: ResolvedSubscription | null
        site?: AuthSiteSummary | null
        sites?: AuthAccessibleSite[]
        modules?: EffectiveModule[]
      },
      bound?: OwnerSiteBound,
    ) {
      const owner = bound ?? bindOwnerSite()
      this.user = payload.user
      this.phone = payload.user.phone
      this.isAuthenticated = true
      this.subscription = mapSubscription(payload.subscription)
      this.site = payload.site ?? null
      this.sites = payload.sites ?? []
      owner.modules.value = payload.modules ?? []
      owner.siteId.value = payload.site?.id ?? null
    },

    async hydrate() {
      const bound = bindOwnerSite()
      try {
        // Prefer useRequestFetch on SSR so the sealed session cookie is forwarded.
        const requestFetch = import.meta.server ? useRequestFetch() : $fetch
        const me = await requestFetch<{
          user: AuthUser
          subscription: ResolvedSubscription | null
          site: AuthSiteSummary | null
          sites?: AuthAccessibleSite[]
          modules: EffectiveModule[]
        }>('/api/me')
        this.applyMe(me, bound)
      } catch {
        this.reset(bound)
      }
      this.hydrated = true
    },

    async logout() {
      const bound = bindOwnerSite()
      try {
        await $fetch('/api/auth/logout', { method: 'POST' })
      } catch {
        // session already gone
      }
      if (import.meta.client) localStorage.removeItem(AUTH_PHONE_KEY)
      this.phone = null
      this.reset(bound)
      this.hydrated = true
    },

    async updateProfile(partial: Partial<AuthUser>) {
      const updated = await $fetch<{ ok: boolean; user: AuthUser }>('/api/me', {
        method: 'PATCH',
        body: {
          name: partial.name,
          firstName: partial.firstName,
          lastName: partial.lastName,
          nationalId: partial.nationalId,
          email: partial.email,
          medicalCouncilNo: partial.medicalCouncilNo,
          specialty: partial.specialty,
          education: partial.education,
        },
      })
      if (this.user) this.user = { ...this.user, ...updated.user }
    },
  },
})
